import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Mail, Calendar, Send, Search, Archive, Star, 
  Trash2, Clock, CheckCircle2, XCircle, Loader2,
  User, Building, MessageSquare, AlertCircle
} from 'lucide-react'
import { blink } from '@/lib/blink'
import { safeJsonParse } from '@/lib/utils';

interface APITestResult {
  id: string
  timestamp: Date
  endpoint: string
  method: string
  status: 'pending' | 'success' | 'error'
  duration?: number
  request?: any
  response?: any
  error?: string
}

interface UserAccount {
  id: string
  provider: string
  email: string
  accessToken: string
  connected: boolean
}

export default function LiveGraphAPIMonitor() {
  const [user, setUser] = useState<any>(null)
  const [accounts, setAccounts] = useState<UserAccount[]>([])
  const [selectedAccount, setSelectedAccount] = useState<string>('')
  const [testResults, setTestResults] = useState<APITestResult[]>([])
  const [isRunning, setIsRunning] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiResponse, setAiResponse] = useState('')
  const [loading, setLoading] = useState(true)

  // Sample test prompts
  const samplePrompts = [
    "Search for emails from 'john' and reply to the most recent one",
    "Get my calendar events for today and create a task for the first meeting",
    "Archive all newsletter emails from the last week",
    "Star all emails with 'urgent' in the subject",
    "Send an email to np@naible.ai with subject 'Test from AI Agent'"
  ]

  useEffect(() => {
    loadUserAndAccounts()
  }, [])

  const loadUserAndAccounts = async () => {
    try {
      setLoading(true)
      const userData = await blink.auth.me()
      setUser(userData)

      const accountsData = await blink.db.emailAccounts.list({
        where: { userId: userData.id },
        orderBy: { createdAt: 'desc' }
      })

      setAccounts(accountsData.map((acc: any) => ({
        id: acc.id,
        provider: acc.provider,
        email: acc.emailAddress,
        accessToken: acc.accessToken,
        connected: Number(acc.syncEnabled) > 0
      })))

      if (accountsData.length > 0) {
        setSelectedAccount(accountsData[0].id)
      }
    } catch (error) {
      console.error('Failed to load user/accounts:', error)
      addTestResult({
        endpoint: 'Auth/Accounts',
        method: 'GET',
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    } finally {
      setLoading(false)
    }
  }

  const addTestResult = (result: Omit<APITestResult, 'id' | 'timestamp'>) => {
    const newResult: APITestResult = {
      id: `test-${Date.now()}-${Math.random()}`,
      timestamp: new Date(),
      ...result
    }
    setTestResults(prev => [newResult, ...prev])
    return newResult
  }

  const executeGraphAPICall = async (
    endpoint: string,
    method: string,
    body?: any
  ): Promise<APITestResult> => {
    const startTime = Date.now()
    const result = addTestResult({
      endpoint,
      method,
      status: 'pending',
      request: body
    })

    try {
      const account = accounts.find(a => a.id === selectedAccount)
      if (!account) {
        throw new Error('No account selected')
      }

      const functionUrl = `https://blink-ben3ou87.functions.blink.new`
      let response: any

      // Route to appropriate function based on endpoint
      if (endpoint.includes('/messages')) {
        if (method === 'POST' && endpoint.includes('/send')) {
          response = await fetch(`${functionUrl}/outlook-send`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${await blink.auth.getToken()}`
            },
            body: JSON.stringify({
              accountId: account.id,
              ...body
            })
          }).then(r => r.json())
        } else if (method === 'GET' && endpoint.includes('$search')) {
          const searchQuery = endpoint.split('$search=')[1]?.split('&')[0] || ''
          response = await fetch(`${functionUrl}/outlook-search`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${await blink.auth.getToken()}`
            },
            body: JSON.stringify({
              accountId: account.id,
              query: decodeURIComponent(searchQuery)
            })
          }).then(r => r.json())
        }
      } else if (endpoint.includes('/calendar')) {
        response = await fetch(`${functionUrl}/calendar-sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await blink.auth.getToken()}`
          },
          body: JSON.stringify({
            userId: user.id,
            accountId: account.id,
            syncType: 'manual'
          })
        }).then(r => r.json())
      }

      const duration = Date.now() - startTime

      setTestResults(prev =>
        prev.map(r =>
          r.id === result.id
            ? {
                ...r,
                status: 'success',
                duration,
                response
              }
            : r
        )
      )

      return { ...result, status: 'success', duration, response }
    } catch (error) {
      const duration = Date.now() - startTime
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'

      setTestResults(prev =>
        prev.map(r =>
          r.id === result.id
            ? {
                ...r,
                status: 'error',
                duration,
                error: errorMsg
              }
            : r
        )
      )

      return { ...result, status: 'error', duration, error: errorMsg }
    }
  }

  const runBasicTests = async () => {
    setIsRunning(true)
    setTestResults([])

    const tests = [
      {
        name: 'List Messages',
        endpoint: '/me/messages?$top=10',
        method: 'GET'
      },
      {
        name: 'Search Messages',
        endpoint: '/me/messages?$search="subject:test"&$top=5',
        method: 'GET'
      },
      {
        name: 'Get Calendar Events',
        endpoint: '/me/calendar/events?$top=10',
        method: 'GET'
      },
      {
        name: 'Get User Profile',
        endpoint: '/me',
        method: 'GET'
      }
    ]

    for (const test of tests) {
      await executeGraphAPICall(test.endpoint, test.method)
      await new Promise(resolve => setTimeout(resolve, 500))
    }

    setIsRunning(false)
  }

  const runAIPromptTest = async () => {
    if (!aiPrompt.trim()) return

    setIsRunning(true)
    setAiResponse('')
    
    try {
      // Stream AI response
      let fullResponse = ''
      
      await blink.ai.streamText(
        {
          prompt: `You are an email AI agent. The user wants: "${aiPrompt}"
          
Available actions:
- searchEmails(query: string)
- sendEmail(to: string, subject: string, body: string)
- replyToEmail(messageId: string, body: string)
- archiveEmail(messageId: string)
- starEmail(messageId: string)
- deleteEmail(messageId: string)
- getCalendarEvents(startDate: string, endDate: string)
- createTask(title: string, dueDate: string)

Analyze the request and output the EXACT API calls needed in this format:
ACTION: <action_name>
PARAMS: <json_params>

Then I will execute them.`,
          maxTokens: 1000
        },
        (chunk) => {
          fullResponse += chunk
          setAiResponse(fullResponse)
        }
      )

      // Parse AI response for actions
      const actions = parseAIActions(fullResponse)
      
      // Execute each action
      for (const action of actions) {
        await executeActionFromAI(action)
        await new Promise(resolve => setTimeout(resolve, 1000))
      }

    } catch (error) {
      console.error('AI prompt test failed:', error)
      addTestResult({
        endpoint: 'AI Agent',
        method: 'STREAM',
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    } finally {
      setIsRunning(false)
    }
  }

  const parseAIActions = (response: string): Array<{action: string, params: any}> => {
    const actions: Array<{action: string, params: any}> = []
    const lines = response.split('\n')
    
    let currentAction = ''
    let currentParams = ''
    
    for (const line of lines) {
      if (line.startsWith('ACTION:')) {
        if (currentAction && currentParams) {
          try {
            actions.push({
              action: currentAction,
              params: safeJsonParse(currentParams)
            })
          } catch (e) {
            console.error('Failed to parse params:', currentParams)
          }
        }
        currentAction = line.replace('ACTION:', '').trim()
        currentParams = ''
      } else if (line.startsWith('PARAMS:')) {
        currentParams = line.replace('PARAMS:', '').trim()
      }
    }
    
    // Add last action
    if (currentAction && currentParams) {
      try {
        actions.push({
          action: currentAction,
          params: safeJsonParse(currentParams)
        })
      } catch (e) {
        console.error('Failed to parse params:', currentParams)
      }
    }
    
    return actions
  }

  const executeActionFromAI = async (action: {action: string, params: any}) => {
    const { action: actionName, params } = action

    switch (actionName) {
      case 'searchEmails':
        await executeGraphAPICall(
          `/me/messages?$search="${params.query}"&$top=10`,
          'GET'
        )
        break
      
      case 'sendEmail':
        await executeGraphAPICall(
          '/me/messages/send',
          'POST',
          {
            to: params.to,
            subject: params.subject,
            body: params.body
          }
        )
        break
      
      case 'getCalendarEvents':
        await executeGraphAPICall(
          `/me/calendar/events?$filter=start/dateTime ge '${params.startDate}' and end/dateTime le '${params.endDate}'`,
          'GET'
        )
        break
      
      default:
        console.log('Unknown action:', actionName)
    }
  }

  const getStatusIcon = (status: APITestResult['status']) => {
    switch (status) {
      case 'pending':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />
    }
  }

  const getStatusBadge = (status: APITestResult['status']) => {
    const variants: Record<APITestResult['status'], any> = {
      pending: 'default',
      success: 'default',
      error: 'destructive'
    }
    return (
      <Badge variant={variants[status]} className="capitalize">
        {status}
      </Badge>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-yellow-400" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Please sign in to test Graph APIs</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => blink.auth.login()}>Sign In</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Live Graph API Monitor</h1>
          <p className="text-gray-600 mt-1">
            Test Microsoft Graph APIs with your authenticated account
          </p>
        </div>

        {/* User Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Authenticated User
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{user.email}</p>
                <p className="text-sm text-gray-500">User ID: {user.id}</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  className="px-3 py-2 border rounded-lg"
                  disabled={accounts.length === 0}
                >
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.provider} - {account.email}
                    </option>
                  ))}
                </select>
                {accounts.length === 0 && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      No email accounts connected. Please connect an account first.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Testing Interface */}
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="basic">Basic API Tests</TabsTrigger>
            <TabsTrigger value="ai">AI Agent Tests</TabsTrigger>
          </TabsList>

          {/* Basic Tests */}
          <TabsContent value="basic" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Run Basic Graph API Tests</CardTitle>
                <CardDescription>
                  Execute common Graph API operations to verify connectivity
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={runBasicTests}
                  disabled={isRunning || accounts.length === 0}
                  className="w-full"
                >
                  {isRunning ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Running Tests...
                    </>
                  ) : (
                    'Run All Basic Tests'
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Agent Tests */}
          <TabsContent value="ai" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>AI Agent Command Interface</CardTitle>
                <CardDescription>
                  Give natural language commands to the AI agent
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>AI Prompt</Label>
                  <Textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="Enter your command for the AI agent..."
                    rows={3}
                    className="mt-2"
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  {samplePrompts.map((prompt, i) => (
                    <Button
                      key={i}
                      variant="outline"
                      size="sm"
                      onClick={() => setAiPrompt(prompt)}
                    >
                      {prompt}
                    </Button>
                  ))}
                </div>

                <Button
                  onClick={runAIPromptTest}
                  disabled={isRunning || !aiPrompt.trim() || accounts.length === 0}
                  className="w-full"
                >
                  {isRunning ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      AI Processing...
                    </>
                  ) : (
                    'Execute AI Command'
                  )}
                </Button>

                {aiResponse && (
                  <div className="mt-4">
                    <Label>AI Response</Label>
                    <div className="mt-2 p-4 bg-gray-50 rounded-lg border">
                      <pre className="text-sm whitespace-pre-wrap">{aiResponse}</pre>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Test Results */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>API Call Results</span>
              <Badge variant="outline">{testResults.length} calls</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <div className="space-y-3">
                {testResults.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No API calls yet. Run some tests to see results.
                  </div>
                ) : (
                  testResults.map((result) => (
                    <Card key={result.id} className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(result.status)}
                          <span className="font-medium">{result.method}</span>
                          <span className="text-sm text-gray-500">
                            {result.endpoint}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(result.status)}
                          {result.duration && (
                            <Badge variant="outline">{result.duration}ms</Badge>
                          )}
                        </div>
                      </div>

                      <div className="text-xs text-gray-500 mb-2">
                        {result.timestamp.toLocaleTimeString()}
                      </div>

                      {result.error && (
                        <Alert variant="destructive" className="mt-2">
                          <AlertDescription>{result.error}</AlertDescription>
                        </Alert>
                      )}

                      {result.response && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-sm text-blue-600">
                            View Response
                          </summary>
                          <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-auto max-h-48">
                            {JSON.stringify(result.response, null, 2)}
                          </pre>
                        </details>
                      )}

                      {result.request && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-sm text-blue-600">
                            View Request
                          </summary>
                          <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-auto max-h-48">
                            {JSON.stringify(result.request, null, 2)}
                          </pre>
                        </details>
                      )}
                    </Card>
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
